import React, { Component } from 'react';
import { getUsersProjects, isUserAdmin, isUserOwner } from '../../helpers';
import { MARKETING_MODAL, PAYROLL_MODAL, USER_MODAL } from '../../redux/reducers/modal';
import MarketingStatsModal from '../blocks/marketing/MarketingStatsModal';
import ReduxModal from '../blocks/modal/ReduxModal';
import MainNav from '../blocks/navigation/MainNav';
import PayrollGeneratorModal from '../blocks/payroll/PayrollGeneratorModal';
import UsersContent from '../blocks/users/UsersContent';
import UsersModal from '../blocks/users/UsersModal';
import type { User, UserRequest, UsersProps } from '../../types';

type State = {
  modal: {
    title: string,
    edit: boolean,
    saveButtonText: string,
    user: User | null,
  },
  searchForm: {
    role: string,
    projectId: number,
    inactive: boolean,
    student: boolean,
  },
  filteredUsers: Array<User>,
  searchTriggered: boolean,
  triggeredRole: string,
  triggeredProjectId: any,
};

class Users extends Component<UsersProps, State> {
  state: State = {
    modal: {
      title: '',
      edit: false,
      saveButtonText: '',
      user: null,
    },
    searchForm: {
      role: 'member',
      projectId: 0,
      inactive: false,
      student: false,
    },

    filteredUsers: this.props.users.filter(user => user.active),
    searchTriggered: false,
    triggeredRole: '',
    triggeredProjectId: null,
  };

  handlePayrollGeneratorClick = () => {
    const { openModal } = this.props;
    openModal({ id: PAYROLL_MODAL });
  };

  handlePayrollModalClose = () => {
    const { closeModal } = this.props;
    closeModal({ id: PAYROLL_MODAL });
  };

  handleMarketingStatsClick = () => {
    const { openModal } = this.props;
    openModal({ id: MARKETING_MODAL });
  };

  handleMarketingModalClose = () => {
    const { closeModal } = this.props;
    closeModal({ id: MARKETING_MODAL });
  };

  handleEditModalOpen = (user: User) => {
    const { openModal, users } = this.props;
    const freshUser = users.find(u => u.id === user.id) || user;
    openModal({ id: USER_MODAL });
    this.setState({
      modal: {
        title: 'Edit user',
        edit: true,
        saveButtonText: 'Save',
        user: freshUser,
      },
    });
  };

  handleModalClose = () => {
    const { closeModal } = this.props;
    closeModal({ id: USER_MODAL });
    this.setState({
      modal: {
        title: '',
        edit: false,
        saveButtonText: '',
        user: null,
      },
    });
  };

  handleAddModalOpen = () => {
    const { openModal } = this.props;
    openModal({ id: USER_MODAL });
    this.setState({
      modal: {
        title: 'Add user',
        edit: false,
        saveButtonText: 'Save new user',
        user: null,
      },
    });
  };

  handleUserSubmit = (formData: UserRequest) => {
    const { modal: { edit }, searchTriggered } = this.state;
    const { onAddUser, onEditUser } = this.props;
    this.disableUsersModalButtons();
    const afterEditActions = () => {
      if (searchTriggered) this.setState({ filteredUsers: this.getFilteredUsers() });
      this.handleModalClose();
    };
    const afterAddActions = () => {
      this.handleSearchUsersReset();
      this.handleModalClose();
    };
    if (edit) {
      onEditUser(formData).then(afterEditActions);
    } else {
      onAddUser(formData).then(afterAddActions);
    }
  };

  handleUserDelete = (userId: number) => {
    const { searchTriggered } = this.state;
    const { onRemoveUser } = this.props;
    this.disableUsersModalButtons();
    const afterRemoveActions = () => {
      if (searchTriggered) this.setState({ filteredUsers: this.getFilteredUsers() });
      this.handleModalClose();
    };
    onRemoveUser(userId).then(afterRemoveActions);
  };

  disableUsersModalButtons = () => {
    const { updateModal } = this.props;
    updateModal({
      id: USER_MODAL,
      disableButtons: true,
    });
  };

  handleSelectRoleChange = (role: string) => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, role },
    });
  };

  handleSelectProjectChange = (projectId: number) => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, projectId },
    });
  };

  handleSearchUsersSubmit = () => {
    const { searchForm: { role, projectId } } = this.state;
    this.setState({
      filteredUsers: this.getFilteredUsers(),
      searchTriggered: true,
      triggeredRole: role,
      triggeredProjectId: projectId,
    });
  };

  handleSearchUsersReset = () => {
    this.setState({
      filteredUsers: [],
      searchTriggered: false,
      triggeredRole: '',
      triggeredProjectId: null,
    });
  };

  handleInactiveChange = () => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, inactive: !searchForm.inactive },
    });
  };

  handleStudentChange = () => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, student: !searchForm.student },
    });
  };

  getFilteredUsers = () => {
    const {
      searchForm: {
        role, projectId, inactive, student,
      },
    } = this.state;
    let { users } = this.props;
    users = users.filter(user => inactive || user.active);
    if (student) {
      users = users.filter(user => user.student);
    }
    if (role === 'any' && projectId === 0) {
      return users;
    }
    if (role !== 'any' && projectId === 0) {
      return users.filter(user => user.role === role);
    }
    if (role === 'any' && projectId !== 0) {
      return users.filter(user => user.projectIds.includes(projectId));
    }

    return users.filter(user => user.role === role && user.projectIds.includes(projectId));
  };

  render(): React.ReactNode {
    const {
      currentUser, roles, vacations,
      router, projects,
    } = this.props;
    const {
      modal, searchForm, filteredUsers, searchTriggered, triggeredRole, triggeredProjectId,
    } = this.state;
    const isAdmin = isUserAdmin(currentUser);
    const isOwner = isUserOwner(currentUser);
    const userProjects = modal.user
      ? getUsersProjects(modal.user, projects)
      : [];
    const userVacations = modal.user ? vacations[modal.user.id] : {};

    return (
      <div className="users-view container-view view-wrapper">
        <MainNav
          currentUser={currentUser}
          navigate={router.navigate}
          onPayrollGeneratorClick={this.handlePayrollGeneratorClick}
          onMarketingStatsClick={this.handleMarketingStatsClick}
        />
        <UsersContent
          isAdmin={isAdmin}
          isOwner={isOwner}
          users={filteredUsers}
          selectedRole={searchForm.role}
          selectedProjectId={searchForm.projectId}
          onEditModalOpen={this.handleEditModalOpen}
          onAddModalOpen={this.handleAddModalOpen}
          onSelectRoleChange={this.handleSelectRoleChange}
          onSelectProjectChange={this.handleSelectProjectChange}
          onSearchUsersSubmit={this.handleSearchUsersSubmit}
          onSearchUsersReset={this.handleSearchUsersReset}
          onInactiveChange={this.handleInactiveChange}
          onStudentChange={this.handleStudentChange}
          inactive={searchForm.inactive}
          student={searchForm.student}
          roles={roles}
          projects={projects}
          searchTriggered={searchTriggered}
          triggeredRole={triggeredRole}
          triggeredProjectId={triggeredProjectId}
        />
        <ReduxModal id={USER_MODAL}>
          <UsersModal
            title={modal.title}
            edit={modal.edit}
            saveButtonText={modal.saveButtonText}
            user={modal.user}
            vacations={userVacations}
            projects={projects}
            userProjects={userProjects as any}
            roles={roles}
            onUserSubmit={this.handleUserSubmit}
            onUserDelete={this.handleUserDelete}
            onCloseModal={this.handleModalClose}
          />
        </ReduxModal>
        <ReduxModal id={PAYROLL_MODAL}>
          <PayrollGeneratorModal
            onCloseModal={this.handlePayrollModalClose}
          />
        </ReduxModal>
        <ReduxModal id={MARKETING_MODAL}>
          <MarketingStatsModal
            onCloseModal={this.handleMarketingModalClose}
          />
        </ReduxModal>
      </div>
    );
  }
}

export default Users;
